import './ReviewTableMuiX.scss';
import React, {useEffect, useMemo} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "~/stores";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers-pro";
import type {GridInitialState, GridSlotProps} from "@mui/x-data-grid-premium";
import {
    DataGridPremium,
    GRID_AGGREGATION_FUNCTIONS,
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    useGridApiRef
} from "@mui/x-data-grid-premium";
import {getCategorizationTableColumns} from "./columns/CategorizationTableColumns.ts";
import {
    type CategorizationTableSettings,
    getCategorizationTableInitialSettings,
    storeCategorizationTableSettings
} from "./settings/CategorizationTableSettings.ts";
import {CategorizationReviewTableToolbar} from "./toolbar/CategorizationReviewTableToolbar.tsx";
import type {DeserializedGridColDef} from "~/components/muix-data-grid/MuiXColumnDefinitions.ts";
import {toReviewMuiXColumn} from "./columns/toReviewMuiXColumn.tsx";
import type {CustomReviewColumnTypes} from "./columns/ColumnTypes";
import {
    applyLocalCategorizationColumnSettings,
    storeColumnWidthChange
} from "./settings/CategorizationColumnSettings.ts";
import type {AsyncGridRowModel, PartGridGroupModel, PartGridRowModel} from "./CategorizationTableDataModel.ts";
import {getCategorizationReviewTableDataModel, GROUP_KEY, GROUP_VAL} from "./CategorizationTableDataModel.ts";
import {
    CATEGORIZATION_TABLE_DEFAULT_PAGE_SIZE,
    CATEGORIZATION_TABLE_PAGE_OPTIONS
} from "./CategorizationTableConstants.ts";
import {isReviewRowOpenForReview} from "~/services/classes/MatReviewHelpers.ts";
import {getGroupByHeaderColDef} from "./columns/GroupByHeaderColDef.tsx";
import {toJS} from "mobx";
import {CategorizationTableDebug} from "./CategorizationTableDebug.tsx";
import {getDayJsLocale} from "~/utils/day-js-utils.ts";
import {Box} from "@mui/material";

type C = DeserializedGridColDef<CustomReviewColumnTypes>;

declare module '@mui/x-data-grid-premium' {
    // noinspection JSUnusedGlobalSymbols
    interface ToolbarPropsOverrides {
        settings: CategorizationTableSettings;
        onSettingsChange: React.Dispatch<React.SetStateAction<CategorizationTableSettings>>;
    }
}

export const CategorizationReviewTableMuiX: React.FC = observer(() => {
    const {p, authStore, bagStore, categorizationReviewStore} = useStores()
    const tableController = categorizationReviewStore.reviewPageController.reviewPageTableController;
    const selectionController = categorizationReviewStore.reviewPageController.reviewPageSelectionController;
    const filterController = categorizationReviewStore.reviewPageController.reviewPageFilterController;

    const initialState: GridInitialState = useMemo<GridInitialState>(() => ({
        pagination: {
            paginationModel: {
                pageSize: CATEGORIZATION_TABLE_DEFAULT_PAGE_SIZE
            },
        },
    }), []);

    const apiRef = useGridApiRef();
    React.useEffect(() => {
        // Link the apiRef to the store, so that the store can use it
        // noinspection TypeScriptUnresolvedReference
        categorizationReviewStore.reviewPageController.reviewPageTableController.gridApi = apiRef.current ?? undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiRef]);

    const databag = categorizationReviewStore.bagId;
    const revision =
        typeof categorizationReviewStore.reviewPageController.reviewPageRevisionController.revision === 'number' ?
            categorizationReviewStore.reviewPageController.reviewPageRevisionController.revision : undefined;
    const showAsStaff = authStore.isMithraStaffGroup;
    const showSelection = !categorizationReviewStore.viewOnly;
    const groupBy = categorizationReviewStore.reviewPageController.isGroupBy;

    // https://mui.com/x/react-data-grid/row-grouping/#custom-grouping-column
    const groupByColDef = React.useMemo(() => getGroupByHeaderColDef(p.supplierDataName, p.p.s_name_col_width),
        [p.supplierDataName, p.p.s_name_col_width]
    );
    console.log('bagStore.categorizationColumns', bagStore.categorizationColumns)
    const columns: C[] = React.useMemo(() =>
            tableController.applyColumnOrdering(
                applyLocalCategorizationColumnSettings(
                    getCategorizationTableColumns(p, bagStore, showAsStaff, databag, showSelection, groupBy)
                        .map(c => toReviewMuiXColumn(c, showAsStaff)), groupBy
                )
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [databag, showAsStaff, showSelection, groupBy, tableController.columnOrder]
    );

    const dataSource = React.useMemo(() =>
            databag ? getCategorizationReviewTableDataModel(databag, revision, categorizationReviewStore) : undefined,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [databag, revision]
    );

    const [settings, setSettings] = React.useState<CategorizationTableSettings>(getCategorizationTableInitialSettings());
    React.useEffect(() => storeCategorizationTableSettings(settings), [settings]);

    React.useEffect(() => {
        // On Mount
        return () => {
            // On Unmount (e.g. remove from DOM)
            tableController.init();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const gridApi = apiRef.current;
        if (!gridApi) return;

        const scheduleAutosizeCategoryColumns = () => {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    void gridApi.autosizeColumns({
                        columns: ['previous_cat', 'cat'],
                        includeHeaders: true,
                        disableColumnVirtualization: true,
                    });
                })
            );
        };

        return gridApi.subscribeEvent('rowsSet', scheduleAutosizeCategoryColumns);
    }, [apiRef, databag, revision]);

    const filterModel = toJS(filterController.gridFilterModel);
    const isLoadingData = categorizationReviewStore.reviewPageController.isLoadingData;

    return <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getDayJsLocale(p.p.locale)}>
        <CategorizationTableDebug columns={columns}/>
        {/*
        Parent flex slot + percentage width avoids 100vw vs layout viewport mismatch on zoom (page-level horizontal scroll).
        MUI Data Grid still needs a bounded width: 100% of the main content column is sufficient (see layout docs).
        */}
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                height: 'calc(100vh - 64px - 24px - 16px - 24px)',
                minWidth: 0,
                boxSizing: 'border-box',
            }}
        >
            <DataGridPremium
                columnBufferPx={800}
                className="review-table-muix auto-row-height-centered"
                initialState={initialState}
                getRowId={(row) => tableController.rowId(row)}

                // pinning
                pinnedColumns={tableController.pinnedColumns}
                onPinnedColumnsChange={(pinnedColumns, details) => tableController.onPinnedColumnsChange(pinnedColumns, details)}

                // col width
                onColumnWidthChange={params => storeColumnWidthChange(params)}
                // col order
                onColumnOrderChange={tableController.storeColumnOrderChange}

                // sorting
                sortModel={tableController.sortModel}
                onSortModelChange={(sortModel, details) => tableController.onSortModelChange(sortModel, details)}

                // Column visibility
                columnVisibilityModel={tableController.visibilityModel}
                onColumnVisibilityModelChange={(model, details) => tableController.onVisibilityModelChange(model, details)}
                // Pagination
                pagination
                // Grouping
                groupingColDef={groupByColDef}
                rowGroupingModel={tableController.rowGroupingModel}

                // Grouping - aggregation
                aggregationFunctions={GRID_AGGREGATION_FUNCTIONS}
                aggregationModel={tableController.aggregationModel}

                columns={columns}
                autosizeOptions={{
                    columns: ['p_name', 'p_description'],
                    includeHeaders: false,
                }}
                apiRef={apiRef}
                dataSource={dataSource}
                dataSourceCache={null}
                loading={isLoadingData}
                checkboxSelection={showSelection}
                checkboxSelectionVisibleOnly
                keepNonExistentRowsSelected // This means we need to clear the selection ourselves when needed

                // Selection
                isRowSelectable={params =>
                    params.row
                    && showSelection
                    && !(params.row as AsyncGridRowModel).isLoading
                    && !(params.row as AsyncGridRowModel).hotfixV8DisableInteractions
                    && !(params.row as PartGridRowModel).subGroupPagination
                    && isReviewRowOpenForReview(params.row as PartGridRowModel)
                }
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                onRowSelectionModelChange={(rowSelectionModel, details) => selectionController.onRowSelectionModelChange(rowSelectionModel, details)}
                rowSelectionPropagation={{parents: true, descendants: true}}

                density={settings.density}
                showToolbar
                slots={{
                    toolbar: CategorizationReviewTableToolbar,
                }}
                slotProps={{
                    toolbar: {
                        settings,
                        onSettingsChange: setSettings,
                    },
                    columnsManagement: {
                        getTogglableColumns: columns => columns
                            .map(c => c.field)
                            .filter(f =>
                                // Hide columns with __
                                !f.startsWith('__')
                                // When grouping, the GROUP_KEY and GROUP_VAL should not be toggleable
                                && (!groupBy || (f !== GROUP_KEY && f !== GROUP_VAL))
                            ),
                    },
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                    basePagination: {
                        disabled: tableController.muixCustomizedPaginationDisabled,
                        count: tableController.muixCustomizedPaginationCount,
                        page: tableController.muixCustomizedPaginationPage,
                        rowsPerPage: tableController.muixCustomizedPaginationRowsPerPage,
                        rowsPerPageOptions: CATEGORIZATION_TABLE_PAGE_OPTIONS,
                        onPageChange: (_e, p) => tableController.muixCustomizedPaginationOnPageChange(p),
                        onRowsPerPageChange: (rpp) => tableController.muixCustomizedPaginationOnRowsPerPageChange(rpp),
                        showFirstButton: true,
                        showLastButton: true,
                    },
                } as unknown as GridSlotProps}

                disablePivoting
                headerFilters
                filterModel={filterModel}
                onFilterModelChange={(model, details) => filterController.onFilterModelChange(model, details)}
                onDataSourceError={error => categorizationReviewStore.reviewPageController.onError(error)}

                getRowClassName={(params) => {
                    if (!params.row) return '';
                    const row = params.row as PartGridRowModel | PartGridGroupModel;
                    return 'muix-row'
                        + (row.isOutOfSync ? ' out-of-sync-row' : '')
                        + (row.hotfixV8DisableInteractions ? ' hotfix-v8-disable-interactions' : '')
                        + (!row.isLoading && !row.isOutOfSync && !row.hotfixV8DisableInteractions && (
                            row['group'] !== undefined) ? ' parent-row' : '')
                        + (tableController.paginationDisabledRowIds.has(tableController.rowId(params.row)) ? ' pagination-disabled-row' : '')
                        + (tableController.paginationHiddenRowIds.has(tableController.rowId(params.row)) ? ' hidden-row' : '')
                        + (row.subGroupPagination ? ' sub-group-pagination-row' : '')
                }}
                getCellClassName={(params) => {
                    if (!params.row) return '';
                    const row = params.row as PartGridRowModel | PartGridGroupModel;
                    return 'muix-cell'
                        + (params.field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD ? ' grouping-cell' : '')
                        + (params.rowNode.type === 'leaf' ? ' leaf-cell' : '')
                        // Hide all other cells for the MoreRowsDummy row
                        + (row.subGroupPagination && params.field !== GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD ? ' hidden-cell' : '')
                        + (row.subGroupPagination && params.field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD ? ' sub-group-pagination' : '')
                }}

                getRowHeight={() => 'auto'}
            />
        </Box>
    </LocalizationProvider>
});
